import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Instagram, Store, Loader2, AlertCircle } from 'lucide-react';
import BuyerCollectionModal from '../components/shared/BuyerCollectionModal';
import { getBuyerId } from '../lib/push';

// Feb 2026 — Buyer-facing seller storefront. Opened by the "Catalog" icon
// in the chat header (CollectionView) so buyers can browse every catalogue
// the seller has published. Uses the existing collections data via a new
// public endpoint GET /api/sellers/{sid}/catalog. Each card opens a STABLE
// browse modal (Aug 2026) that mirrors Dashboard → Catalogue Broadcast's
// CollectionDetailModal — products stay visible while browsing and closing
// returns to the collection list. No chat/AI/cart/quotation involved.
export default function SellerCatalog() {
  const { sid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [openCid, setOpenCid] = useState(null);   // collection being browsed

  
useEffect(() => {
  if (!sid) return;

  const base = process.env.REACT_APP_BACKEND_URL;
  const sessionId = getBuyerId();

  axios.get(`${base}/api/sellers/${sid}/catalog`, {
    params: { session_id: sessionId },
  })
    .then(({ data }) => setData(data))
    .catch((e) => setErr(e?.response?.data?.detail || 'Failed to load catalogue'));
}, [sid]);


  if (err) return (
    <div className="min-h-screen bg-[#efe7dc] flex items-center justify-center p-4 text-center">
      <div className="bg-white/90 rounded-xl p-5 max-w-sm">
        <AlertCircle className="w-8 h-8 text-neutral-500 mx-auto" />
        <div className="mt-2 font-semibold text-neutral-800">{err}</div>
      </div>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen bg-[#efe7dc] flex items-center justify-center">
      <Loader2 className="w-6 h-6 text-neutral-500 animate-spin" />
    </div>
  );

  const s = data.seller || {};
  const sellerName = s.business_name || s.name || 'FABTEXS Seller';
  const initials = (sellerName || 'FS').split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  return (
    <div className="min-h-screen bg-[#efe7dc]" data-testid="seller-catalog-page">
      <header
        className="sticky top-0 z-20 bg-[#f7f2eb]/95 backdrop-blur-md border-b border-black/10 flex items-center gap-2 px-2 sm:px-3 py-2 sm:py-2.5"
        data-testid="seller-catalog-header"
      >
        <button
          onClick={() => nav(-1)}
          className="p-2 -ml-1 rounded-full hover:bg-black/5"
          aria-label="Back"
          data-testid="seller-catalog-back"
        ><ArrowLeft className="w-5 h-5 text-neutral-800" /></button>
        <div className="relative shrink-0">
          <div className="w-11 h-11 rounded-full bg-[#25D366] text-white font-bold flex items-center justify-center text-[15px] shadow-inner ring-2 ring-white">
            {initials}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <div className="font-semibold text-[15px] sm:text-[16px] text-neutral-900 truncate" data-testid="seller-catalog-name">
              {sellerName}
            </div>
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-[#1DA1F2] shrink-0" aria-hidden="true">
              <path fill="currentColor" d="M23 12l-2.44-2.79.34-3.69-3.61-.82-1.89-3.2L12 2.96 8.6 1.5 6.71 4.69 3.1 5.5l.34 3.7L1 12l2.44 2.79-.34 3.7 3.61.82L8.6 22.5 12 21.04l3.4 1.46 1.89-3.19 3.61-.82-.34-3.7L23 12zm-13 5l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
            </svg>
          </div>
          <div className="text-[12px] text-neutral-600 -mt-0.5 flex items-center gap-1.5">
            <span>Catalogue</span>
            {(s.rating_count || 0) > 0 && s.rating_average != null && (
              <span className="inline-flex items-center gap-0.5 text-neutral-700">
                <span className="text-neutral-400">·</span>
                <span className="text-amber-500">★</span>
                <span className="font-semibold">{Number(s.rating_average).toFixed(1)}</span>
                <span className="text-neutral-500">({s.rating_count})</span>
              </span>
            )}
          </div>
        </div>
        {s.instagram_url && (
          <a
            href={s.instagram_url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-full hover:bg-black/5 inline-flex text-neutral-700"
            aria-label="Instagram"
            data-testid="seller-catalog-instagram"
          ><Instagram className="w-5 h-5" /></a>
        )}
      </header>

      <main className="max-w-3xl mx-auto px-3 sm:px-4 py-4">
        <div className="flex items-center gap-2 mb-3">
          <Store className="w-4 h-4 text-neutral-700" />
          <div className="text-[13px] font-semibold text-neutral-800">
            {data.count} {data.count === 1 ? 'collection' : 'collections'}
          </div>
        </div>

        {data.count === 0 ? (
          <div className="bg-white/90 rounded-xl p-6 text-center text-[13px] text-neutral-600" data-testid="seller-catalog-empty">
            This seller hasn&apos;t published any collections yet.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="seller-catalog-grid">
            {data.collections.map((c) => (
              <button
                key={c.id}
                onClick={() => setOpenCid(c.short_code || c.id)}
                className="text-left bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                data-testid={`seller-catalog-card-${c.id}`}
              >
                <div className="aspect-square bg-neutral-100 flex items-center justify-center overflow-hidden">
                  {c.cover_image_url ? (
                    <img src={c.cover_image_url} alt={c.name || 'Collection'} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <Store className="w-8 h-8 text-neutral-400" />
                  )}
                </div>
                <div className="p-2.5">
                  <div className="text-[13px] font-semibold text-neutral-900 line-clamp-1">
                    {c.name || 'Collection'}
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-0.5">
                    {c.product_count} {c.product_count === 1 ? 'product' : 'products'}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      <BuyerCollectionModal cid={openCid} onClose={() => setOpenCid(null)} />
    </div>
  );
}
